import { type NavigationGuardNext, type RouteLocationAsRelativeGeneric, type RouteLocationNormalized, type RouteLocationRaw } from "vue-router";
import type { LanguageCode } from "@/services/translation";
import { CountryCode, CountryCode2LanguageCodeMap, FallbackCountryCode, FallbackLanguageCode } from "@/services/translation";
import { createLoginLink } from "@/router/linkFactories";
import { saveAfterAuthRedirect } from "@/services/afterAuthRedirectService";
import { captureException } from "@/services/analytics/sentry";
import type { UserSettings } from "@/services/user/settings/types";
import { useUserSettings } from "@/composables/user/userSettings";
import { checkUserOnInitialPageLoad } from "@/services/user/auth/login/helpers";
import { useAppStore } from "@/composables/appStore";
import { getEnumKeyByValue, isStringInEnum } from "@/utilities/enum";

export const isPublic = (route: RouteLocationNormalized): boolean => route.meta?.public === true;

export const createLocalizedRoute = (route: RouteLocationAsRelativeGeneric): RouteLocationRaw => {
  if (route.params === undefined || !route.params.countryCode) {
    // @todo Do not use FallbackCountryCode. It sohuld not be possible to create links when country code is not determined.
    // This is just a temp fallback.
    const countryCode = useAppStore().countryCode.value ?? FallbackCountryCode;
    route.params = route.params ?? {};
    route.params.countryCode = countryCode;
  }
  return route;
};

export const addHashToRoute = (route: RouteLocationRaw, hash: string): RouteLocationRaw => ({
  ...(route as object),
  hash,
});

export const goBackInHistory = () => window.history.back();

export const getLocalizationObject = (route: RouteLocationNormalized): { language: LanguageCode; country: CountryCode } | undefined => {
  const countryCodeString = route.params.countryCode as string;
  if (isStringInEnum(countryCodeString, CountryCode)) {
    const countryCode = getEnumKeyByValue(countryCodeString, CountryCode);
    const languageCode = CountryCode2LanguageCodeMap[countryCode];
    return { language: languageCode, country: countryCode };
  } else if (route.meta.allowNoCountryCode === true) {
    return { language: FallbackLanguageCode, country: FallbackCountryCode };
  } else {
    return undefined;
  }
};

export const redirectToAuth = (route: RouteLocationNormalized, next: NavigationGuardNext) => {
  const redirectQueryParam = route.fullPath;
  if (route.meta.returnAfterRegistration === true) {
    void saveAfterAuthRedirect(route.fullPath).catch((e: unknown) => {
      console.error("[Auth] Saving the after-auth redirect failed", e);
      captureException(e);
    });
  }
  return next(createLoginLink(redirectQueryParam));
};

export const getUserSettingsForRouter = async (): Promise<UserSettings | undefined> => {
  await checkUserOnInitialPageLoad(); // check if there is any token stored and possibly try to authenticate
  return useUserSettings().userSettings.value;
};
